import React from 'react'
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { useTranslations } from 'next-intl'
import { travelersDocTypeOptionsMap } from '@/src/constants/travelers'

// Interface for the transformed passenger data
export interface TransformedPassenger {
    id: string
    type: 'ADT' | 'CHD' | 'INF'
    firstName: string
    lastName: string
    passportNumber?: string
    documentType?: string
    documentNumber?: string
    frequentFlyerNumber?: string
}

export interface PassengerDetailsProps {
    passengers: TransformedPassenger[]
}

export const PassengerDetails: React.FC<PassengerDetailsProps> = ({ passengers }) => {
    const t = useTranslations('confirmationPage')
    const tDoc = useTranslations('itineraryPage.travelerDetailsRecord')

    if (!passengers?.length) return null

    const typeConfig: Record<TransformedPassenger['type'], string> = {
        ADT: t('adult'),
        CHD: t('child'),
        INF: t('infant')
    }

    const firstDocType = (passengers[0]?.documentType || '').toUpperCase() as keyof typeof travelersDocTypeOptionsMap
    const firstDocTranslationKey = travelersDocTypeOptionsMap[firstDocType]
    const documentHeaderLabel = firstDocTranslationKey ? tDoc(`documentType.${firstDocTranslationKey}`) : t('document')

    const renderDesktopView = () => (
        <TableContainer>
            <Table size="small" sx={{ minWidth: 300 }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 600, pl: 2, py: 1 }}>{t('passengerName')}</TableCell>
                        <TableCell sx={{ fontWeight: 600, pr: 2, py: 1 }}>{documentHeaderLabel}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {passengers.map(passenger => (
                        <TableRow
                            key={passenger.id}
                            hover
                            sx={{
                                '&:last-child td': { border: 0 },
                                '&:hover': { backgroundColor: 'action.hover' }
                            }}
                        >
                            <TableCell sx={{ pl: 2, py: 1.5, fontWeight: 500 }}>
                                {passenger.firstName} {passenger.lastName}
                            </TableCell>
                            <TableCell sx={{ pr: 2, py: 1.5 }}>{passenger.documentNumber || '—'}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 0.5, md: 0.5 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper'
            }}
        >
            {renderDesktopView()}
        </Paper>
    )
}

export default PassengerDetails
